import { m } from "motion/react";
import { useCallback, useMemo, useState } from "react";

import { CategoryListItem } from "../components/category-list-item";
import { Icon } from "../components/icon";
import { MobileIslandSwipe } from "../components/mobile-island-swipe";
import { menuAnimation } from "../lib/const";
import { useGlobal } from "../lib/global-context";
import { useBackEscape } from "../lib/hooks/use-back-escape";
import { useIsMobile } from "../lib/hooks/use-breakpoints";
import type { VoidComponent } from "../lib/types";
import { booleanAttribute } from "../lib/utils";

interface Props {
	params: { pillar: string };
}

const PillarRoute: VoidComponent<Props> = ({ params }) => {
	useBackEscape({ to: "/", params });

	const { pillars, exploreCategoriesTitle } = useGlobal();

	const [isDescriptionOpen, setIsDescriptionOpen] = useState(false);

	const pillar = useMemo(
		() => pillars.find((p) => p.slug.current === params.pillar),
		[pillars, params.pillar],
	);

	const isMobile = useIsMobile();

	const handleToggleDescription = useCallback(() => {
		if (!isMobile) {
			return;
		}

		setIsDescriptionOpen((prev) => !prev);
	}, [isMobile]);

	return (
		<>
			{isMobile && <MobileIslandSwipe />}

			<div className="scroll-container">
				<m.div
					{...menuAnimation(isMobile)}
					className="flex flex-1 flex-col justify-between gap-8 py-3 desktop:py-8"
				>
					<div className="flex flex-col gap-2 desktop:gap-3">
						{pillar?.title && (
							<button
								type="button"
								tabIndex={isMobile ? 0 : -1}
								onClick={handleToggleDescription}
								className="flex cursor-pointer items-center gap-2 text-foreground select-text desktop:cursor-text"
								aria-expanded={isDescriptionOpen}
								aria-label={isDescriptionOpen ? "Collapse description" : "Expand description"}
							>
								<h1 className="flex-1 text-left text-xl desktop:text-3xl">{pillar.title}</h1>

								<Icon
									name={isDescriptionOpen ? "subtract" : "add"}
									className="size-6 shrink-0 desktop:hidden"
								/>
							</button>
						)}

						<div
							className="grid grid-rows-[1fr] transition-[grid-template-rows] duration-300 ease-out data-collapsed:grid-rows-[0fr] desktop:data-collapsed:grid-rows-[1fr]"
							data-collapsed={booleanAttribute(!isDescriptionOpen)}
						>
							<div className="overflow-hidden">
								{pillar?.description && (
									<p className="font-serif text-sm text-foreground desktop:text-xl">
										{pillar.description}
									</p>
								)}
							</div>
						</div>
					</div>

					<div className="flex flex-col gap-4">
						<h2 className="text-xs text-foreground desktop:tracking-[0.02em]">
							{exploreCategoriesTitle}
						</h2>

						<ul className="flex flex-col gap-0.5">
							{pillar?.categories.map((category) => (
								<CategoryListItem
									key={category._key}
									category={category}
									pillarSlug={params.pillar}
								/>
							))}
						</ul>
					</div>
				</m.div>
			</div>
		</>
	);
};

export { PillarRoute };
